/**
 * Support module for Permission Codes.
 * This file provides shared implementation details for its surrounding feature or platform area.
 */
export const ROLE_PERMISSION_CODES = {
  usersCreateAdmin: 'users.create.admin',
  usersCreateSupervisor: 'users.create.supervisor',
  usersCreateAgent: 'users.create.agent',
  usersCreateBtl: 'users.create.btl',
  usersCreateLogistics: 'users.create.logistics',
  usersRead: 'users.read',
  usersReferenceRead: 'users.reference.read',
  usersUpdate: 'users.update',
  usersUpdateStatus: 'users.update-status',
  usersDelete: 'users.delete',
  rolesRead: 'roles.read',
  rolesCreate: 'roles.create',
  rolesUpdate: 'roles.update',
  rolesDelete: 'roles.delete',
  permissionsCatalogRead: 'permissions.catalog.read',
  sessionsRead: 'sessions.read',
  sessionsRevoke: 'sessions.revoke',
  supervisorsApprove: 'supervisors.approve',
  supervisorsReject: 'supervisors.reject',
  agenciesRead: 'aggregation.agencies.read',
  agenciesReferenceRead: 'aggregation.agencies.reference.read',
  agenciesCreate: 'aggregation.agencies.create',
  agenciesUpdate: 'aggregation.agencies.update',
  agenciesDelete: 'aggregation.agencies.delete',
  agenciesAssignmentsRead: 'aggregation.agencies.assignments.read',
  posRead: 'aggregation.pos.read',
  posCreate: 'aggregation.pos.create',
  posUpdate: 'aggregation.pos.update',
  posDelete: 'aggregation.pos.delete',
  prospectsRead: 'aggregation.prospects.read',
  prospectsCreate: 'aggregation.prospects.create',
  prospectsUpdate: 'aggregation.prospects.update',
  prospectsDelete: 'aggregation.prospects.delete',
  prospectsApprove: 'aggregation.prospects.approve',
  prospectsReject: 'aggregation.prospects.reject',
  tasksRead: 'aggregation.tasks.read',
  tasksCreate: 'aggregation.tasks.create',
  tasksUpdate: 'aggregation.tasks.update',
  tasksDelete: 'aggregation.tasks.delete',
  targetsRead: 'aggregation.targets.read',
  targetsCreate: 'aggregation.targets.create',
  targetsUpdate: 'aggregation.targets.update',
  targetsDelete: 'aggregation.targets.delete',
  targetsReadOwned: 'aggregation.targets.read.owned',
  targetsUpdateOwned: 'aggregation.targets.update.owned',
  targetsDeleteOwned: 'aggregation.targets.delete.owned',
  taskActivitiesCreate: 'aggregation.tasks.activities.create',
  taskApprovalSubmit: 'aggregation.tasks.approval.submit',
  taskApprovalReview: 'aggregation.tasks.approval.review',
  aggregationLogisticsDashboardRead: 'aggregation.logistics.dashboard.read',
  aggregationLogisticsTasksRead: 'aggregation.logistics.tasks.read',
  aggregationLogisticsTasksCreate: 'aggregation.logistics.tasks.create',
  aggregationLogisticsTasksUpdate: 'aggregation.logistics.tasks.update',
  aggregationLogisticsTasksApprovalSubmit: 'aggregation.logistics.tasks.approval.submit',
  aggregationLogisticsTasksApprovalReview: 'aggregation.logistics.tasks.approval.review',
  aggregationLogisticsRoutesRead: 'aggregation.logistics.routes.read',
  aggregationLogisticsRoutesActivitiesCreate: 'aggregation.logistics.routes.activities.create',
  aggregationLogisticsRoutesApprove: 'aggregation.logistics.routes.approve',
  aggregationLogisticsRoutesReject: 'aggregation.logistics.routes.reject',
  aggregationLogisticsRoutesChangeRequestsCreate: 'aggregation.logistics.routes.change-requests.create',
  aggregationLogisticsRoutesChangeRequestsReview: 'aggregation.logistics.routes.change-requests.review',
  aggregationLogisticsVehiclesRead: 'aggregation.logistics.vehicles.read',
  aggregationLogisticsVehiclesCreate: 'aggregation.logistics.vehicles.create',
  aggregationLogisticsVehiclesUpdate: 'aggregation.logistics.vehicles.update',
  aggregationLogisticsVehicleDocumentsRead: 'aggregation.logistics.vehicle-documents.read',
  aggregationLogisticsVehicleDocumentsCreate: 'aggregation.logistics.vehicle-documents.create',
  aggregationLogisticsVehicleDocumentsUpdate: 'aggregation.logistics.vehicle-documents.update',
  aggregationLogisticsVehicleDocumentsDelete: 'aggregation.logistics.vehicle-documents.delete',
  aggregationLogisticsFuelRequestsRead: 'aggregation.logistics.fuel-requests.read',
  aggregationLogisticsFuelRequestsCreate: 'aggregation.logistics.fuel-requests.create',
  aggregationLogisticsFuelRequestsUpdate: 'aggregation.logistics.fuel-requests.update',
  aggregationLogisticsFuelRequestsReview: 'aggregation.logistics.fuel-requests.review',
  aggregationLogisticsMaintenanceRequestsRead: 'aggregation.logistics.maintenance-requests.read',
  aggregationLogisticsMaintenanceRequestsCreate: 'aggregation.logistics.maintenance-requests.create',
  aggregationLogisticsMaintenanceRequestsReview: 'aggregation.logistics.maintenance-requests.review',
  aggregationLogisticsMaintenanceTypesRead: 'aggregation.logistics.maintenance-types.read',
  aggregationLogisticsMaintenanceTypesCreate: 'aggregation.logistics.maintenance-types.create',
  aggregationLogisticsMaintenanceTypesUpdate: 'aggregation.logistics.maintenance-types.update',
  aggregationLogisticsMaintenanceTypesDelete: 'aggregation.logistics.maintenance-types.delete',
  btlDashboardRead: 'aggregation.btl.dashboard.read',
  btlRead: 'aggregation.btl.read',
  btlCreate: 'aggregation.btl.create',
  btlUpdate: 'aggregation.btl.update',
  btlDelete: 'aggregation.btl.delete',
  btlActivitiesCreate: 'aggregation.btl.activities.create',
  btlStatusUpdate: 'aggregation.btl.status.update',
  btlSubmissionsRead: 'aggregation.btl.submissions.read',
  btlSubmissionsCreate: 'aggregation.btl.submissions.create',
  btlSubmissionsReview: 'aggregation.btl.submissions.review',
  btlNotesCreate: 'aggregation.btl.notes.create',
  inventoryCategoriesRead: 'inventory.categories.read',
  inventoryCategoriesCreate: 'inventory.categories.create',
  inventoryCategoriesUpdate: 'inventory.categories.update',
  inventoryCategoriesDelete: 'inventory.categories.delete',
  inventoryProductsRead: 'inventory.products.read',
  inventoryProductsCreate: 'inventory.products.create',
  inventoryProductsUpdate: 'inventory.products.update',
  inventoryProductsDelete: 'inventory.products.delete',
  inventoryVariantsRead: 'inventory.variants.read',
  inventoryVariantsCreate: 'inventory.variants.create',
  inventoryVariantsUpdate: 'inventory.variants.update',
  inventoryVariantsDelete: 'inventory.variants.delete',
  inventoryCentralsRead: 'inventory.centrals.read',
  inventoryCentralReferenceRead: 'inventory.central.reference.read',
  inventoryCentralsCreate: 'inventory.centrals.create',
  inventoryVehiclesRead: 'inventory.vehicles.read',
  inventoryVehiclesCreate: 'inventory.vehicles.create',
  inventoryAgencyStockRead: 'inventory.agency-stock.read',
  inventoryAgentStockRead: 'inventory.agent-stock.read',
  inventoryCentralStockRead: 'inventory.central-stock.read',
  inventoryAgencyTransactionsRead: 'inventory.agency-transactions.read',
  inventoryAgencyTransactionsCreate: 'inventory.agency-transactions.create',
  inventoryAgentTransactionsRead: 'inventory.agent-transactions.read',
  inventoryAgentTransactionsCreate: 'inventory.agent-transactions.create',
  inventoryCentralTransactionsRead: 'inventory.central-transactions.read',
  inventoryCentralTransactionsCreate: 'inventory.central-transactions.create',
  inventoryBankingNotesRead: 'inventory.banking-notes.read',
  inventoryBankingNotesCreate: 'inventory.banking-notes.create',
  inventoryBankingNotesUpdate: 'inventory.banking-notes.update',
  inventoryBankingNotesDelete: 'inventory.banking-notes.delete',
  inventoryDirectSalesRead: 'inventory.direct-sales.read',
  inventoryDirectSalesAssignmentsRead: 'inventory.direct-sales.assignments.read',
  inventoryDirectSalesCustomerLookupRead: 'inventory.direct-sales.customer-lookup.read',
  inventoryDirectSalesCreate: 'inventory.direct-sales.create',
  inventoryLogisticsTasksRead: 'inventory.logistics-tasks.read',
  inventoryLogisticsTasksCreate: 'inventory.logistics-tasks.create',
  inventoryRouteLegActivitiesCreate: 'inventory.route-legs.activities.create',
  inventoryRouteLegApprove: 'inventory.route-legs.approve',
  inventoryRouteLegReject: 'inventory.route-legs.reject',
  agenciesReadOwned: 'aggregation.agencies.read.owned',
  agenciesUpdateOwned: 'aggregation.agencies.update.owned',
  agenciesDeleteOwned: 'aggregation.agencies.delete.owned',
  posReadOwned: 'aggregation.pos.read.owned',
  posUpdateOwned: 'aggregation.pos.update.owned',
  posDeleteOwned: 'aggregation.pos.delete.owned',
  prospectsReadOwned: 'aggregation.prospects.read.owned',
  prospectsUpdateOwned: 'aggregation.prospects.update.owned',
  prospectsDeleteOwned: 'aggregation.prospects.delete.owned',
  tasksReadOwned: 'aggregation.tasks.read.owned',
  tasksUpdateOwned: 'aggregation.tasks.update.owned',
  tasksDeleteOwned: 'aggregation.tasks.delete.owned',
  btlReadOwned: 'aggregation.btl.read.owned',
  btlUpdateOwned: 'aggregation.btl.update.owned',
  btlDeleteOwned: 'aggregation.btl.delete.owned'
} as const;

export type PermissionCode = (typeof ROLE_PERMISSION_CODES)[keyof typeof ROLE_PERMISSION_CODES];
